import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { isEqualWith } from '@Utils/Lodash';
import useInterval from '@Hooks/useInterval';

import { useEditor } from '@Services/Editor';

import { postProjectEvent } from '@Services/Editor/Request';
import { removeTargetEditorInLocalStorage } from '@Services/EditorList';

import { ReactComponent as OfflineSVG } from '@Assets/Svgs/ic_offline.svg';
import { ReactComponent as CheckSVG } from '@Assets/Svgs/selection_check_on.svg';
import TX from '@Components/Common/Text';
import Button from '@Components/Common/Button';
import SnackBar from '@Components/Common/SnackBar';
import { LoadingForButtonLottie } from '@Components/Common/Lottie';
import convertProcessesToEventJSON from '@Services/Editor/EventJsonController';
import { getCurrentTimeStamp } from '@Utils/getCurrentTimeStamp';
import { throttle } from 'lodash';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import history from '@Utils/history';

// const testJson = require('./test.json');

const FIVE_MINUTES_TO_MILLISECONDS = 5 * 60000;

const IGNORE_KEY = 'selectedProcessId';

const ProjectEventPostButton = ({ isNetworkConnected, projectId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const projectIdByParams = searchParams.get('id');

  const editor = useSelector(useEditor);

  const snackBarRef = useRef(null);
  const [prevEditor, setPrevEditor] = useState(editor);
  const [isDataFetching, setIsDataFetching] = useState(false);
  const [isProjectEventPosted, setIsProjectEventPosted] = useState(false);
  const getProjectEventAnyChanged = useCallback(() => {
    return !isEqualWith(editor, prevEditor, (value, other, key) => {
      return key === `${IGNORE_KEY}` ? true : undefined;
    });
  }, [editor, prevEditor]);

  const isProjectEventAnyChanged = useMemo(() => getProjectEventAnyChanged(), [editor, prevEditor]);

  // 드로틀 적용

  // const postProjectEventToS3 = useCallback(async () => {
  //   // 저장 버튼 누를 때, 새로고침 할 때, 브라우저 강제 종료시
  //   if (isThrottled) {
  //     snackBarRef.current.show({
  //       text: '1분 뒤에 다시 시도해주세요.',
  //     });
  //     return;
  //   }
  //   if (!isThrottled) {
  //     console.log('.');
  //     setIsThrottled(true);
  //     const formattedProcesses = JSON.parse(convertProcessesToEventJSON(editor.processes));
  //     const updatedEditor = {
  //       ...editor,
  //       processes: formattedProcesses,
  //     };
  //     try {
  //       const data = {
  //         projectId,
  //         projectEventData: updatedEditor,
  //       };

  //       setIsDataFetching(true);
  //       await postProjectEvent(data);
  //       await removeTargetEditorInLocalStorage(projectId);

  //       setPrevEditor(editor);

  //       setIsDataFetching(false);
  //       setIsProjectEventPosted(true);
  //     } catch (error) {
  //       // TODO: ERROR 로그 S3 전송
  //       setIsDataFetching(false);

  //       snackBarRef.current.show({
  //         text: '저장에 실패했습니다. 다시 시도해 주세요.',
  //       });
  //     } finally {
  //       setTimeout(() => {
  //         setIsThrottled(false);
  //       }, 60000);
  //     }
  //   }d
  // }, [editor, prevEditor]);

  // 기존;
  const postProjectEventToS3 = useCallback(async () => {
    const formattedProcesses = JSON.parse(convertProcessesToEventJSON(editor.processes));
    const updatedEditor = {
      ...editor,
      processes: formattedProcesses,
      updatedAt: getCurrentTimeStamp(),
    };
    // 테스트용 test.json에 processId를 내 것으로 바꿔야함.
    // const updatedEditor = {
    //   ...editor,
    //   processes: testJson.processes,
    // };
    // console.log('formattedProcesses:', formattedProcesses[0].choices[0].results);
    try {
      const data = {
        projectId,
        projectEventData: updatedEditor,
      };

      setIsDataFetching(true);
      await postProjectEvent(data);
      await removeTargetEditorInLocalStorage(projectId);

      setPrevEditor(editor);

      setIsDataFetching(false);
      setIsProjectEventPosted(true);
      snackBarRef.current.show({
        text: '변경사항이 저장되었습니다.',
      });
    } catch (error) {
      console.log('error:', error);
      // TODO: ERROR 로그 S3 전송
      setIsDataFetching(false);

      snackBarRef.current.show({
        text: '저장에 실패했습니다. 다시 시도해 주세요.',
      });
    }
  }, [editor, prevEditor]);

  const tryProjectEventToS3 = useCallback(async () => {
    if (isProjectEventAnyChanged) {
      await postProjectEventToS3();
    }
  }, [isProjectEventAnyChanged, editor, prevEditor]);

  useInterval(() => {
    tryProjectEventToS3();
  }, FIVE_MINUTES_TO_MILLISECONDS);

  useEffect(() => {
    if (isProjectEventAnyChanged) {
      setIsProjectEventPosted(false);
    }
  }, [isProjectEventAnyChanged, editor, prevEditor]);

  // useEffect(() => {
  //   const handleUnload = () => {
  //     // beforeunload 이벤트 핸들러에서 직접 최신의 editor 상태를 참조하여 호출
  //     postProjectEventToS3();

  //     // 추가적인 언로드 로직이 필요하면 여기에 추가
  //   };

  //   window.addEventListener('beforeunload', handleUnload);

  //   return () => {
  //     window.removeEventListener('beforeunload', handleUnload);
  //   };
  // }, [postProjectEventToS3]);

  useEffect(() => {
    const listenBackEvent = () => {
      if (
        window.confirm(
          '나가시기 전에 저장하셨나요? 저장하지 않은 변경 사항이 있을 경우, 모든 작업이 사라질 수 있습니다. 안전을 위해 지금 저장해 주세요!',
        )
      ) {
        // photoHandler.reshootingHandler();
        // photoHandler.countResetHandler();
      } else {
        navigate(`${location.pathname}?id=${projectIdByParams}`);
      }
    };
    const historyEvent = history.listen(() => {
      listenBackEvent();
    });
    return historyEvent;
  }, []);

  // ------------- 쓰로틀
  const lastCallTimeRef = useRef(0);

  const throttledPostProjectEventToS3 = useCallback(
    throttle(
      () => {
        lastCallTimeRef.current = Date.now();
        postProjectEventToS3();
      },
      5000,
      { trailing: false },
    ),
    [postProjectEventToS3],
  );

  const handleKeyDown = useCallback(
    event => {
      if ((event.ctrlKey || event.metaKey || event.altKey) && event.key === 's') {
        event.preventDefault();
        if (Date.now() - lastCallTimeRef.current < 5000) {
          snackBarRef.current.show({
            text: '잠시 후 다시 시도해주세요.',
          });
        } else if (isProjectEventAnyChanged) {
          throttledPostProjectEventToS3();
        }
      }
    },
    [throttledPostProjectEventToS3, isProjectEventAnyChanged],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
  // ------------- 쓰로틀 끝

  const getProjectEventPostButtonComponent = () => {
    if (isProjectEventPosted) {
      return (
        <>
          <CheckIcon />
          <ProjectEventPostButtonText>저장됨</ProjectEventPostButtonText>
        </>
      );
    }

    if (isDataFetching) {
      return (
        <>
          <LoadingForButtonLottie />
          <ProjectEventPostButtonText>저장 중</ProjectEventPostButtonText>
        </>
      );
    }

    if (!isNetworkConnected) {
      return (
        <>
          <NetworkOfflineIcon />
          <ProjectEventPostButtonText>인터넷 연결 끊김</ProjectEventPostButtonText>
        </>
      );
    }

    return <Button text="저장" onClick={postProjectEventToS3} />;
  };

  return (
    <>
      <ProjectEventPostButtonContainer>
        <ProjectEventPostButtonWrapper>{getProjectEventPostButtonComponent()}</ProjectEventPostButtonWrapper>
      </ProjectEventPostButtonContainer>

      <SnackBar ref={snackBarRef} />
    </>
  );
};

ProjectEventPostButton.propTypes = {
  isNetworkConnected: PropTypes.bool.isRequired,
  projectId: PropTypes.string.isRequired,
};

const FlexAlignCenterDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ProjectEventPostButtonContainer = styled.div`
  display: flex;
  position: absolute;
  top: 10px;
  right: 14px;
`;

const ProjectEventPostButtonWrapper = styled(FlexAlignCenterDiv)`
  flex-direction: row;
  padding: 6px 12px;
  gap: 8px;
`;

const ProjectEventPostButtonText = styled(TX.FootnoteM)`
  color: ${({ theme }) => theme.colors.ui_on_4};
`;

const NetworkOfflineIcon = styled(OfflineSVG)``;

const CheckIcon = styled(CheckSVG)`
  fill: ${({ theme }) => theme.colors.ui_on_4};
`;

export default ProjectEventPostButton;

// import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
// import { useSelector, useDispatch } from 'react-redux';
// import styled from 'styled-components';
// import PropTypes from 'prop-types';

// import { isEqualWith } from '@Utils/Lodash';
// import useInterval from '@Hooks/useInterval';

// import { useEditor } from '@Services/Editor';

// import { postProjectEvent } from '@Services/Editor/Request';
// import { removeTargetEditorInLocalStorage } from '@Services/EditorList';

// import { ReactComponent as OfflineSVG } from '@Assets/Svgs/ic_offline.svg';
// import { ReactComponent as CheckSVG } from '@Assets/Svgs/selection_check_on.svg';
// import TX from '@Components/Common/Text';
// import Button from '@Components/Common/Button';
// import SnackBar from '@Components/Common/SnackBar';
// import { LoadingForButtonLottie } from '@Components/Common/Lottie';
// import convertProcessesToEventJSON from '@Services/Editor/EventJsonController';
// import { getCurrentTimeStamp } from '@Utils/getCurrentTimeStamp';
// import { throttle } from 'lodash';

// // const testJson = require('./test.json');

// const FIVE_MINUTES_TO_MILLISECONDS = 5 * 60000;

// const IGNORE_KEY = 'selectedProcessId';

// const ProjectEventPostButton = ({ isNetworkConnected, projectId }) => {
//   const dispatch = useDispatch();

//   const editor = useSelector(useEditor);

//   const snackBarRef = useRef(null);
//   const [prevEditor, setPrevEditor] = useState(editor);
//   const [isDataFetching, setIsDataFetching] = useState(false);
//   const [isProjectEventPosted, setIsProjectEventPosted] = useState(false);
//   const getProjectEventAnyChanged = useCallback(() => {
//     return !isEqualWith(editor, prevEditor, (value, other, key) => {
//       return key === `${IGNORE_KEY}` ? true : undefined;
//     });
//   }, [editor, prevEditor]);

//   const isProjectEventAnyChanged = useMemo(() => getProjectEventAnyChanged(), [editor, prevEditor]);

//   // 드로틀 적용

//   // const postProjectEventToS3 = useCallback(async () => {
//   //   // 저장 버튼 누를 때, 새로고침 할 때, 브라우저 강제 종료시
//   //   if (isThrottled) {
//   //     snackBarRef.current.show({
//   //       text: '1분 뒤에 다시 시도해주세요.',
//   //     });
//   //     return;
//   //   }
//   //   if (!isThrottled) {
//   //     console.log('.');
//   //     setIsThrottled(true);
//   //     const formattedProcesses = JSON.parse(convertProcessesToEventJSON(editor.processes));
//   //     const updatedEditor = {
//   //       ...editor,
//   //       processes: formattedProcesses,
//   //     };
//   //     try {
//   //       const data = {
//   //         projectId,
//   //         projectEventData: updatedEditor,
//   //       };

//   //       setIsDataFetching(true);
//   //       await postProjectEvent(data);
//   //       await removeTargetEditorInLocalStorage(projectId);

//   //       setPrevEditor(editor);

//   //       setIsDataFetching(false);
//   //       setIsProjectEventPosted(true);
//   //     } catch (error) {
//   //       // TODO: ERROR 로그 S3 전송
//   //       setIsDataFetching(false);

//   //       snackBarRef.current.show({
//   //         text: '저장에 실패했습니다. 다시 시도해 주세요.',
//   //       });
//   //     } finally {
//   //       setTimeout(() => {
//   //         setIsThrottled(false);
//   //       }, 60000);
//   //     }
//   //   }d
//   // }, [editor, prevEditor]);

//   // 기존;
//   const postProjectEventToS3 = useCallback(async () => {
//     const formattedProcesses = JSON.parse(convertProcessesToEventJSON(editor.processes));
//     const updatedEditor = {
//       ...editor,
//       processes: formattedProcesses,
//       updatedAt: getCurrentTimeStamp(),
//     };
//     // 테스트용 test.json에 processId를 내 것으로 바꿔야함.
//     // const updatedEditor = {
//     //   ...editor,
//     //   processes: testJson.processes,
//     // };
//     // console.log('formattedProcesses:', formattedProcesses[0].choices[0].results);
//     try {
//       const data = {
//         projectId,
//         projectEventData: updatedEditor,
//       };

//       setIsDataFetching(true);
//       await postProjectEvent(data);
//       await removeTargetEditorInLocalStorage(projectId);

//       setPrevEditor(editor);

//       setIsDataFetching(false);
//       setIsProjectEventPosted(true);
//       snackBarRef.current.show({
//         text: '변경사항이 저장되었습니다.',
//       });
//     } catch (error) {
//       // TODO: ERROR 로그 S3 전송
//       setIsDataFetching(false);

//       snackBarRef.current.show({
//         text: '저장에 실패했습니다. 다시 시도해 주세요.',
//       });
//     }
//   }, [editor, prevEditor]);

//   const tryProjectEventToS3 = useCallback(async () => {
//     if (isProjectEventAnyChanged) {
//       await postProjectEventToS3();
//     }
//   }, [isProjectEventAnyChanged, editor, prevEditor]);

//   useInterval(() => {
//     tryProjectEventToS3();
//   }, FIVE_MINUTES_TO_MILLISECONDS);

//   useEffect(() => {
//     if (isProjectEventAnyChanged) {
//       setIsProjectEventPosted(false);
//     }
//   }, [isProjectEventAnyChanged, editor, prevEditor]);

//   useEffect(() => {
//     // unmout 시 저장되게하기
//     const handleUnload = () => {
//       // beforeunload 이벤트 핸들러에서 직접 최신의 editor 상태를 참조하여 호출
//       postProjectEventToS3();

//       // 추가적인 언로드 로직이 필요하면 여기에 추가
//     };

//     window.addEventListener('beforeunload', handleUnload);

//     return () => {
//       window.removeEventListener('beforeunload', handleUnload);
//     };
//   }, [postProjectEventToS3, dispatch]);

//   // ------------- 쓰로틀
//   const lastCallTimeRef = useRef(0);

//   const throttledPostProjectEventToS3 = useCallback(
//     throttle(
//       () => {
//         lastCallTimeRef.current = Date.now();
//         postProjectEventToS3();
//       },
//       5000,
//       { trailing: false },
//     ),
//     [postProjectEventToS3],
//   );

//   const handleKeyDown = useCallback(
//     event => {
//       if ((event.ctrlKey || event.metaKey || event.altKey) && event.key === 's') {
//         event.preventDefault();
//         if (Date.now() - lastCallTimeRef.current < 5000) {
//           snackBarRef.current.show({
//             text: '잠시 후 다시 시도해주세요.',
//           });
//         } else if (isProjectEventAnyChanged) {
//           throttledPostProjectEventToS3();
//         }
//       }
//     },
//     [throttledPostProjectEventToS3, isProjectEventAnyChanged],
//   );

//   useEffect(() => {
//     window.addEventListener('keydown', handleKeyDown);
//     return () => {
//       window.removeEventListener('keydown', handleKeyDown);
//     };
//   }, [handleKeyDown]);
//   // ------------- 쓰로틀 끝

//   const getProjectEventPostButtonComponent = () => {
//     if (isProjectEventPosted) {
//       return (
//         <>
//           <CheckIcon />
//           <ProjectEventPostButtonText>저장됨</ProjectEventPostButtonText>
//         </>
//       );
//     }

//     if (isDataFetching) {
//       return (
//         <>
//           <LoadingForButtonLottie />
//           <ProjectEventPostButtonText>저장 중</ProjectEventPostButtonText>
//         </>
//       );
//     }

//     if (!isNetworkConnected) {
//       return (
//         <>
//           <NetworkOfflineIcon />
//           <ProjectEventPostButtonText>인터넷 연결 끊김</ProjectEventPostButtonText>
//         </>
//       );
//     }

//     return <Button text="저장" onClick={postProjectEventToS3} />;
//   };

//   return (
//     <>
//       <ProjectEventPostButtonContainer>
//         <ProjectEventPostButtonWrapper>{getProjectEventPostButtonComponent()}</ProjectEventPostButtonWrapper>
//       </ProjectEventPostButtonContainer>

//       <SnackBar ref={snackBarRef} />
//     </>
//   );
// };

// ProjectEventPostButton.propTypes = {
//   isNetworkConnected: PropTypes.bool.isRequired,
//   projectId: PropTypes.string.isRequired,
// };

// const FlexAlignCenterDiv = styled.div`
//   display: flex;
//   justify-content: center;
//   align-items: center;
// `;

// const ProjectEventPostButtonContainer = styled.div`
//   display: flex;
//   position: absolute;
//   top: 10px;
//   right: 14px;
// `;

// const ProjectEventPostButtonWrapper = styled(FlexAlignCenterDiv)`
//   flex-direction: row;
//   padding: 6px 12px;
//   gap: 8px;
// `;

// const ProjectEventPostButtonText = styled(TX.FootnoteM)`
//   color: ${({ theme }) => theme.colors.ui_on_4};
// `;

// const NetworkOfflineIcon = styled(OfflineSVG)``;

// const CheckIcon = styled(CheckSVG)`
//   fill: ${({ theme }) => theme.colors.ui_on_4};
// `;

// export default ProjectEventPostButton;
